import React, { Suspense } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as z from 'zod';

import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { displayToastError } from '@/helpers/display-toast';
import { AuthErrorCode, signIn } from '@/lib/auth';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from '@/lib/constants/legal-urls';
import { UnauthenticatedRoutes } from '@/router/routes';
import { useRootActorRef, useRootSelector } from '@/router/state/root/util/context/helpers';

import { LocalizedException } from '../../../common/exceptions/localized-exception';
import { signInFormSchema } from '../validation/sign-in-form-schema';

type SignInFormValues = z.infer<typeof signInFormSchema>;

export const SignInPage: React.FC = () => {
  const { t } = useTranslation(['unauthenticated.signIn', 'unauthenticated.common']);
  const actorRef = useRootActorRef();

  const isRootStateLoading = useRootSelector(state => state.context.isLoading);

  const form = useForm<SignInFormValues>({
    resolver: zodResolver(signInFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const signInMutation = useMutation({
    mutationFn: async (values: SignInFormValues) => {
      return await signIn(values.email, values.password);
    },
    onSuccess: user => {
      actorRef.send({ type: 'LOGGED_IN', user });
    },
    onError: (error: Error | LocalizedException) => {
      let message: string;
      if (error instanceof LocalizedException) {
        switch (error.code) {
          case AuthErrorCode.NotAuthorized:
            message = t('error.invalidCredentials');
            break;
          case AuthErrorCode.UserNotConfirmed:
            message = t('error.userNotConfirmed');
            break;
          case AuthErrorCode.UserNotFound:
            message = t('error.userNotFound');
            break;
          default:
            message = error.message || t('error.unknown');
        }
      } else {
        message = error.message || t('error.unknown');
      }
      displayToastError(message);
    },
  });

  const handleSubmit: SubmitHandler<SignInFormValues> = values => {
    signInMutation.mutate(values);
  };

  return (
    <div className="flex min-h-screen flex-1 flex-col items-center justify-center p-8">
      <Suspense fallback={<Loader2 className="animate-spin" />}>
        <div className="w-full max-w-sm">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <h1 className="text-2xl font-semibold text-foreground">{t('header')}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{t('subtitle')}</p>
              <div className="mt-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('emailLabel')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t('emailPlaceholder')}
                          autoComplete="username"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="mt-4">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel>{t('passwordLabel')}</FormLabel>
                        <Link
                          to={UnauthenticatedRoutes.ResetPassword}
                          className="text-sm text-foreground underline"
                        >
                          {t('forgotPassword')}
                        </Link>
                      </div>
                      <FormControl>
                        <PasswordInput
                          {...field}
                          placeholder={t('passwordPlaceholder')}
                          autoComplete="current-password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <ButtonLoadable
                className="mt-4 w-full"
                type="submit"
                isLoading={signInMutation.isPending || isRootStateLoading}
              >
                {t('continue')}
              </ButtonLoadable>
              <div className="mt-6 px-8 text-center text-sm text-muted-foreground">
                <Trans
                  i18nKey="agreement"
                  t={t}
                  components={{
                    terms: (
                      <a
                        href={TERMS_OF_SERVICE_URL}
                        className="whitespace-nowrap underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    ),
                    privacy: (
                      <a
                        href={PRIVACY_POLICY_URL}
                        className="whitespace-nowrap underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    ),
                  }}
                />
              </div>
            </form>
          </Form>
        </div>
      </Suspense>
    </div>
  );
};
